import React, {useState} from 'react';
import {Link} from 'react-router-dom';

import {
    Box,
    Button,
    Card,
    CardContent, Chip, Container,
    FormControl,
    Grid, IconButton, InputAdornment,
    InputLabel,
    MenuItem, Paper,
    Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Tooltip, Typography
} from "@mui/material";
import {useSelector} from "react-redux";

import Loading from "../../components/Common/Loading";
import {
    useCreateAudienceMutation,
    useGetAllAudiencesQuery,
    useUpdateAudienceMutation
} from "../../core/api/audience-api";

import {
    useCreateCampaignMutation, useDeleteCampaignMutation,
    useGetAllCampaignsQuery, useScheduleCampaignMutation, useStopCampaignMutation,
    useUpdateCampaignMutation
} from "../../core/api/campaign-api";
import CreateCampaignModal from "./CreateCampaignPage";
import dayjs from "dayjs";

import {
    PlayArrow,
    Stop,
    Edit,
    Cancel,
    Analytics,
    Equalizer
} from "@mui/icons-material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {DataGrid} from "@mui/x-data-grid";
import { LicenseInfo } from '@mui/x-license';
import {DataGridPro} from "@mui/x-data-grid-pro";
import {getCtr} from "../../core/utils/audienceUtils";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
LicenseInfo.setLicenseKey('47ddef6db8f484df94839cf56de77581Tz0xMDkwNTcsRT0xNzcyNzU1MTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=');

const timezone = require('dayjs/plugin/timezone');
const utc = require('dayjs/plugin/utc');
dayjs.extend(timezone);
dayjs.extend(utc);

export default function CampaignsList({status, handleUpdate}) {


    const {
        data,
        isLoading,
        isSuccess,
        isError,
        isFetching,
        error
    } = useGetAllCampaignsQuery({status:status})

    const {
        data: audience,
        isLoading: isLoadingAudience,
        isSuccess: isSuccessAudience,
        isError: isErrorAudience,
        isFetching: isFetchingAudience,
        error: errorAudience
    } = useGetAllAudiencesQuery()

    const [scheduleCampaign, scheduleCampaignResponse] = useScheduleCampaignMutation()
    const [cancelCampaign, cancelCampaignResponse] = useStopCampaignMutation()
    const [deleteCampaign, deleteCampaignResponse] = useDeleteCampaignMutation()

    const handleStart = (campaignId) => {
        scheduleCampaign(campaignId)
    };

    const handleCancel = (campaignId) => {
        cancelCampaign(campaignId)
    };

    const handleDelete = (campaignId) => {
        deleteCampaign(campaignId)
    };

    const handleEdit = (campaignId) => {
        handleUpdate(campaignId)
    };

    const handleStop = (campaignId) => {
        cancelCampaign(campaignId)
    };

    const statusColor = {
        "DRAFT":"default",
        "ACTIVE":"success",
        "SCHEDULED":"warning",
        "COMPLETED":"primary",
        "CANCELLED":"error",
    }

    const getAvailableActions = (campaign) => {
        // Определяем доступные действия в зависимости от статуса
        switch (campaign.status || status) {
            case 'DRAFT':
                return ['start', 'edit','duplicate'];
            case 'ACTIVE':
                return ['cancel', 'edit','duplicate'];
            case 'SCHEDULED':
                return ['cancel','edit','duplicate'];
            case 'COMPLETED':
                return ['duplicate'];
            case 'CANCELLED':
                return ['duplicate'];
            default:
                return ['edit','duplicate'];
        }
    };




    const getPlatformTitle = (row) => {
        if(audience === undefined) return ''
        const audienceItem = audience.find(item => item.id === row.audienceId);
        if(audienceItem === undefined) return

        const platforms = audienceItem.filter?.includeAny.filter(item => item.parameter === 'platform')
        if(platforms == null) return  ""
        if(platforms.length>0) return platforms[0].values.join(', ')

    }


    const envStateData = useSelector(state => state.data.EnvStateData); // Для отображения текущего статуса
    const isProd = envStateData.isProd

    const columns = [
        // {
        //     field: 'id',
        //     headerName: 'ID',
        //     width: 120,
        // },
        {
            field: 'title',
            headerName: 'Name',
            width: 180
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 120
        },
        // {
        //     field: 'createdAt',
        //     headerName: 'Created',
        //     width: 120,
        //     renderCell: (params) => {
        //         return dayjs.unix(params.value/1000).format("YYYY-MM-DD HH:mm");
        //     }
        // },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderHeader: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Status
                    <Tooltip title={
                        <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>COMPLETED</strong> - Раздача кампании полностью завершена, все пуши отправлены
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>CANCELLED</strong> - кампания отменена, отправка пушей завершается
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>SCHEDULED</strong> - Кампания запланирована, формируется список игроков, которые должны получить пуш
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>ACTIVE</strong> - Раздача пушей началась, список игроков, которые должны получить пуш уже сформирован
                            </Typography>
                            <Typography variant="body2">
                                <strong>DRAFT</strong> - Черновик
                            </Typography>
                        </Box>
                    } placement="top">
                        <InfoOutlinedIcon sx={{ ml: 1, fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                </Box>
            ),
            renderCell: (params) => {
                return (
                    <Chip
                        label={params.value.toLowerCase()}
                        color={statusColor[params.value]}
                        variant="contained"
                        size="small"
                    />
                );
            }
        },

        {
            field: 'audienceId',
            headerName: 'Audience',
            width: 100,
            renderCell: (params) => {
                if (!audience) return '';
                const audienceItem = audience.find(item => item.id === params.value);
                return audienceItem ? audienceItem.title : '';
            }
        },
        {
            field: 'platform',
            headerName: 'platform',
            width: 100,
            renderCell: (params) => {
                return getPlatformTitle(params.row)
            }
        },
        {
            field: 'deliveryType',
            headerName: 'Start Type',
            width: 100,
        },
        {
            field: 'deliveryTime',
            headerName: 'Start at',
            width: 140,
            renderCell: (params) => {
                if (Array.isArray(params.value)) {
                    const [year, month, day, hour, minute] = params.value;
                    return dayjs.utc(new Date(Date.UTC(year, month - 1, day, hour, minute))).format("YYYY-MM-DD HH:mm:ss");
                }
                return dayjs.unix(params.value).utc().format("YYYY-MM-DD HH:mm:ss");
            }
        },
        {
            field: 'ctr',
            headerName: 'CTR',
            width: 130,
            renderHeader: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    CTR
                    <Tooltip title={
                        <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Процент игроков, кликнувших на push<br />

                                <strong>clicks / delivered × 100%</strong><br />

                                Обновление каждые 5 минут
                            </Typography>
                        </Box>
                    } placement="top">
                        <InfoOutlinedIcon sx={{ ml: 1, fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                </Box>
            ),
            renderCell: (params) => {
                return getCtr(params.row);
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            minWidth: 200,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const availableActions = getAvailableActions(params.row);

                return (
                    <Box sx={{display: 'flex', gap: 1, pt:1}}>
                        {availableActions.includes('start') && (
                            <Tooltip title="Start Campaign">
                                <IconButton
                                    color="success"
                                    onClick={() => handleStart(params.row.id)}
                                    size="small"
                                >
                                    <PlayArrow/>
                                </IconButton>
                            </Tooltip>
                        )}

                        {availableActions.includes('stop') && (
                            <Tooltip title="Stop Campaign">
                                <IconButton
                                    color="warning"
                                    onClick={() => handleStop(params.row.id)}
                                    size="small"
                                >
                                    <Stop/>
                                </IconButton>
                            </Tooltip>
                        )}

                        {availableActions.includes('edit') && (
                            <Tooltip title="Edit Campaign">
                                <IconButton
                                    color="primary"
                                    component={Link}
                                    to={`/updateCampaign/${params.row.id}`}
                                    size="small"
                                >
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                        )}


                        <Tooltip title="View Analytics">
                            <IconButton
                                color="info"
                                component={Link}
                                to={`/analytics/${params.row.id}`}
                                size="small"
                            >
                                <Equalizer />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Clone Campaign">
                            <IconButton
                                color="info"
                                component={Link}
                                to={`/createCampaign/${params.row.id}/clone`}
                                size="small"
                            >
                                <ContentCopyIcon />
                            </IconButton>
                        </Tooltip>


                        {availableActions.includes('cancel') && (
                            <Tooltip title="Cancel Campaign">
                                <IconButton
                                    color="error"
                                    onClick={() => handleCancel(params.row.id)}
                                    size="small"
                                >
                                    <Cancel/>
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            }
        },
        {
            field: 'delete',
            headerName: '',
            width: 70,
            renderCell: (params) => {
                // Assuming isAdmin is available in the component's scope
                return (!isProd) ? (
                    <IconButton
                        aria-label="edit"
                        onClick={() => handleDelete(params.row.id)}
                    >
                        <DeleteIcon />
                    </IconButton>
                ) : null;
            },
            sortable: false,
            filterable: false,
        }
    ];

    const rows = data || [];


    return <>

        <Loading isLoading={isLoading||isLoadingAudience||isFetching} error={error} isError={error}>
            <DataGrid
                rows={rows}
                columns={columns}
                pageSize={25}
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: 25, page: 0 },
                    },
                }}
                rowsPerPageOptions={[25, 50, 100]}
                disableSelectionOnClick
                sx={{
                    '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                }}
            />


        </Loading>

    </>
}